// ===================================
// KAIXO - Invitar amigos
// Lista los contactos del teléfono con checkboxes.
// Al enviar: abre el sharesheet del sistema o SMS intent con los números.
// ===================================

import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Icons } from '../Icons/Icons';
import useModalClose from '../../hooks/useModalClose';
import './InviteFriendsModal.css';

// Importación dinámica del plugin (no romper en web)
let ContactsPlugin = null;
try {
    // eslint-disable-next-line global-require, import/no-unresolved
    ContactsPlugin = require('@capacitor-community/contacts').Contacts;
} catch { /* plugin no disponible (web) */ }

let Capacitor = null;
try {
    // eslint-disable-next-line global-require
    Capacitor = require('@capacitor/core').Capacitor;
} catch {}

// Plugin de Share nativo (abre el share sheet del sistema operativo).
// Fundamental porque navigator.share a veces no funciona en WebView Android.
let SharePlugin = null;
try {
    // eslint-disable-next-line global-require, import/no-unresolved
    SharePlugin = require('@capacitor/share').Share;
} catch { /* plugin no disponible en web */ }

const I18N = {
    español: {
        title: 'Invitar amigos',
        close: 'Cerrar',
        search: 'Buscar contactos...',
        all: 'Todos', none: 'Ninguno',
        loading: 'Cargando contactos...',
        retry: 'Reintentar',
        shareOther: 'Compartir por otra app',
        otherApp: 'Otra app',
        sms: 'SMS',
        nContacts: (n) => n === 1 ? 'contacto seleccionado' : 'contactos seleccionados',
        noPlugin: 'El acceso a contactos solo funciona en la app móvil (APK). Usa "Compartir por otra app" para enviar la invitación desde aquí.',
        noNative: 'El acceso a contactos solo funciona en la app móvil instalada. Usa "Compartir por otra app".',
        permDenied: 'Has denegado el permiso de contactos. Abre Ajustes de Android → KAIXO → Permisos → Contactos.',
        permFail: 'No se pudo obtener permiso para leer los contactos.',
        noPhones: 'No se encontraron contactos con número de teléfono en tu agenda.',
        noNumbers: 'Ninguno de tus contactos tiene número de teléfono.',
        noContacts: 'No hay contactos disponibles.',
        loadFail: 'Error al cargar contactos: ',
        copyHint: 'Copia el enlace:',
        inviteText: '¡Te invito a KAIXO! Habla con cualquier persona del mundo y cada uno lee en su idioma. Descárgala: ',
        invitePreview: (names, extra) => `Invitación para ${names}${extra}. `,
        andMore: (n) => ` y ${n} más`
    },
    inglés: {
        title: 'Invite friends',
        close: 'Close',
        search: 'Search contacts...',
        all: 'All', none: 'None',
        loading: 'Loading contacts...',
        retry: 'Retry',
        shareOther: 'Share via another app',
        otherApp: 'Other app',
        sms: 'SMS',
        nContacts: (n) => n === 1 ? 'contact selected' : 'contacts selected',
        noPlugin: 'Contact access only works in the mobile app (APK). Use "Share via another app" to send invites from here.',
        noNative: 'Contact access only works in the installed mobile app. Use "Share via another app".',
        permDenied: 'You denied the contacts permission. Open Settings → KAIXO → Permissions → Contacts.',
        permFail: 'Could not get permission to read contacts.',
        noPhones: 'No contacts with phone numbers found in your address book.',
        noNumbers: 'None of your contacts have a phone number.',
        noContacts: 'No contacts available.',
        loadFail: 'Error loading contacts: ',
        copyHint: 'Copy the link:',
        inviteText: 'I invite you to KAIXO! Talk to anyone in the world and each person reads in their language. Download: ',
        invitePreview: (names, extra) => `Invite for ${names}${extra}. `,
        andMore: (n) => ` and ${n} more`
    },
    francés: {
        title: 'Inviter des amis', close: 'Fermer', search: 'Chercher contacts...', all: 'Tous', none: 'Aucun',
        loading: 'Chargement...', retry: 'Réessayer', shareOther: 'Partager via une autre app',
        otherApp: 'Autre app', sms: 'SMS',
        nContacts: (n) => n === 1 ? 'contact sélectionné' : 'contacts sélectionnés',
        noPlugin: 'L\'accès aux contacts marche dans l\'app mobile (APK). Utilise "Partager via une autre app".',
        noNative: 'L\'accès aux contacts marche dans l\'app mobile installée.',
        permDenied: 'Tu as refusé la permission. Réglages → KAIXO → Permissions → Contacts.',
        permFail: 'Impossible d\'obtenir la permission.',
        noPhones: 'Aucun contact avec numéro trouvé.', noNumbers: 'Aucun de tes contacts n\'a de numéro.',
        noContacts: 'Aucun contact disponible.', loadFail: 'Erreur de chargement: ',
        copyHint: 'Copie le lien:',
        inviteText: 'Je t\'invite sur KAIXO ! Parle avec n\'importe qui dans le monde, chacun lit dans sa langue. Télécharge: ',
        invitePreview: (names, extra) => `Invitation pour ${names}${extra}. `,
        andMore: (n) => ` et ${n} de plus`
    },
    alemán: {
        title: 'Freunde einladen', close: 'Schließen', search: 'Kontakte suchen...', all: 'Alle', none: 'Keiner',
        loading: 'Lädt...', retry: 'Erneut', shareOther: 'Über andere App teilen',
        otherApp: 'Andere App', sms: 'SMS',
        nContacts: (n) => n === 1 ? 'Kontakt ausgewählt' : 'Kontakte ausgewählt',
        noPlugin: 'Kontaktzugriff nur in der mobilen App (APK).',
        noNative: 'Kontaktzugriff nur in der installierten App.',
        permDenied: 'Berechtigung verweigert. Einstellungen → KAIXO → Berechtigungen → Kontakte.',
        permFail: 'Berechtigung konnte nicht erteilt werden.',
        noPhones: 'Keine Kontakte mit Telefonnummer gefunden.', noNumbers: 'Keiner deiner Kontakte hat eine Nummer.',
        noContacts: 'Keine Kontakte verfügbar.', loadFail: 'Fehler beim Laden: ',
        copyHint: 'Link kopieren:',
        inviteText: 'Ich lade dich zu KAIXO ein! Sprich mit jedem auf der Welt, jeder liest in seiner Sprache. Lade es runter: ',
        invitePreview: (names, extra) => `Einladung für ${names}${extra}. `,
        andMore: (n) => ` und ${n} weitere`
    },
    portugués: {
        title: 'Convidar amigos', close: 'Fechar', search: 'Pesquisar contactos...', all: 'Todos', none: 'Nenhum',
        loading: 'A carregar...', retry: 'Tentar novamente', shareOther: 'Partilhar via outra app',
        otherApp: 'Outra app', sms: 'SMS',
        nContacts: (n) => n === 1 ? 'contacto selecionado' : 'contactos selecionados',
        noPlugin: 'O acesso a contactos só funciona na app móvel (APK).',
        noNative: 'O acesso a contactos só funciona na app móvel instalada.',
        permDenied: 'Negaste a permissão. Definições → KAIXO → Permissões → Contactos.',
        permFail: 'Não foi possível obter permissão.',
        noPhones: 'Não foram encontrados contactos com número.', noNumbers: 'Nenhum contacto tem número.',
        noContacts: 'Sem contactos disponíveis.', loadFail: 'Erro a carregar: ',
        copyHint: 'Copia o link:',
        inviteText: 'Convido-te para o KAIXO! Fala com qualquer pessoa do mundo, cada um lê no seu idioma. Descarrega: ',
        invitePreview: (names, extra) => `Convite para ${names}${extra}. `,
        andMore: (n) => ` e mais ${n}`
    },
    italiano: {
        title: 'Invita amici', close: 'Chiudi', search: 'Cerca contatti...', all: 'Tutti', none: 'Nessuno',
        loading: 'Caricamento...', retry: 'Riprova', shareOther: 'Condividi con altra app',
        otherApp: 'Altra app', sms: 'SMS',
        nContacts: (n) => n === 1 ? 'contatto selezionato' : 'contatti selezionati',
        noPlugin: 'L\'accesso ai contatti funziona solo nell\'app mobile (APK).',
        noNative: 'L\'accesso ai contatti funziona solo nell\'app mobile installata.',
        permDenied: 'Hai negato il permesso. Impostazioni → KAIXO → Permessi → Contatti.',
        permFail: 'Impossibile ottenere il permesso.',
        noPhones: 'Nessun contatto con numero trovato.', noNumbers: 'Nessuno dei tuoi contatti ha un numero.',
        noContacts: 'Nessun contatto disponibile.', loadFail: 'Errore caricamento: ',
        copyHint: 'Copia il link:',
        inviteText: 'Ti invito su KAIXO! Parla con chiunque nel mondo, ciascuno legge nella sua lingua. Scaricalo: ',
        invitePreview: (names, extra) => `Invito per ${names}${extra}. `,
        andMore: (n) => ` e ${n} altri`
    },
    euskera: {
        title: 'Lagunak gonbidatu', close: 'Itxi', search: 'Kontaktuak bilatu...', all: 'Denak', none: 'Bat ere ez',
        loading: 'Kargatzen...', retry: 'Saiatu berriro', shareOther: 'Beste app batekin partekatu',
        otherApp: 'Beste app', sms: 'SMS',
        nContacts: (n) => n === 1 ? 'kontaktu hautatuta' : 'kontaktu hautatuta',
        noPlugin: 'Kontaktuak atzitzeko app mugikorra behar da (APK).',
        noNative: 'Kontaktuak atzitzeko app mugikorra behar da.',
        permDenied: 'Baimena ukatu duzu. Ezarpenak → KAIXO → Baimenak → Kontaktuak.',
        permFail: 'Ezin izan da baimena lortu.',
        noPhones: 'Ez da telefonodun kontakturik aurkitu.', noNumbers: 'Zure kontakturik ez du telefonorik.',
        noContacts: 'Ez dago kontakturik.', loadFail: 'Kargatzeko errorea: ',
        copyHint: 'Kopiatu esteka:',
        inviteText: 'KAIXOra gonbidatzen zaitut! Hitz egin munduko edonorekin, bakoitzak bere hizkuntzan. Deskargatu: ',
        invitePreview: (names, extra) => `${names}${extra} gonbidapena. `,
        andMore: (n) => ` eta ${n} gehiago`
    }
};
function pickL() {
    try {
        const u = JSON.parse(localStorage.getItem('kaixo_user') || '{}');
        return I18N[u?.language] || I18N.inglés;
    } catch { return I18N.inglés; }
}

function InviteFriendsModal({ currentUser, onClose }) {
    useModalClose(onClose);
    const L = pickL();
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState(new Set());
    const [error, setError] = useState('');

    const isNative = (() => {
        try { return !!(Capacitor && Capacitor.isNativePlatform && Capacitor.isNativePlatform()); }
        catch { return false; }
    })();

    const inviteUrl = useMemo(() => {
        const base = (process.env.REACT_APP_PUBLIC_URL || 'https://backend-dlmm.onrender.com').replace(/\/+$/, '');
        const handle = currentUser?.handle;
        return handle ? `${base}/c/@${handle}` : base;
    }, [currentUser]);

    const inviteText = `${L.inviteText}${inviteUrl}`;

    const loadContacts = async () => {
        setError('');
        setLoading(true);
        if (!ContactsPlugin) {
            setError(L.noPlugin);
            setLoading(false);
            return;
        }
        if (!isNative) {
            setError(L.noNative);
            setLoading(false);
            return;
        }
        try {
            // 1) Consultar estado actual del permiso
            let status = null;
            try {
                if (ContactsPlugin.checkPermissions) {
                    status = await ContactsPlugin.checkPermissions();
                }
            } catch {}

            // 2) Si no está concedido, solicitar (esto dispara el diálogo nativo)
            if (!status || status.contacts !== 'granted') {
                const req = await ContactsPlugin.requestPermissions();
                if (req?.contacts !== 'granted') {
                    setError(req?.contacts === 'denied' ? L.permDenied : L.permFail);
                    setLoading(false);
                    return;
                }
            }

            // 3) Leer contactos
            const result = await ContactsPlugin.getContacts({
                projection: { name: true, phones: true }
            });
            const raw = (result && result.contacts) || [];

            if (raw.length === 0) {
                setError(L.noPhones);
                setLoading(false);
                return;
            }

            const normalized = raw
                .map((c, idx) => {
                    const allPhones = Array.isArray(c.phones) ? c.phones : [];
                    const primaryPhone = allPhones.find(p => p?.isPrimary) || allPhones[0];
                    const number = primaryPhone?.number || '';
                    const display = c.name?.display || c.name?.given || c.name?.family || '(sin nombre)';
                    return {
                        id: c.contactId || `c-${idx}-${number}`,
                        name: display,
                        phone: number
                    };
                })
                .filter(c => c.phone && c.phone.trim())
                .sort((a, b) => a.name.localeCompare(b.name));

            if (normalized.length === 0) {
                setError(L.noNumbers);
            }
            setContacts(normalized);
            setLoading(false);
        } catch (e) {
            setError(L.loadFail + (e?.message || String(e)));
            setLoading(false);
        }
    };

    useEffect(() => {
        loadContacts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isNative]);

    const filtered = useMemo(() => {
        if (!query.trim()) return contacts;
        const q = query.toLowerCase();
        return contacts.filter(c =>
            c.name.toLowerCase().includes(q) || c.phone.replace(/\s/g, '').includes(q.replace(/\s/g, ''))
        );
    }, [contacts, query]);

    const toggle = (id) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleAll = () => {
        if (selected.size === filtered.length) setSelected(new Set());
        else setSelected(new Set(filtered.map(c => c.id)));
    };

    // Envía por SMS a todos los seleccionados.
    // Usamos `smsto:` (estricto) en vez de `sms:` (genérico). `smsto:` se filtra
    // por apps que registran ACTION_SENDTO + scheme "smsto", típicamente sólo
    // las apps de SMS reales (Google Messages, Samsung Messages). Con `sms:`
    // genérico, algunos dispositivos incluyen WhatsApp/Telegram en el chooser
    // porque también manejan ese scheme. Si el usuario tiene un único default
    // de SMS configurado, abre directamente esa app sin chooser.
    const sendSms = () => {
        const selectedContacts = contacts.filter(c => selected.has(c.id));
        if (!selectedContacts.length) return;
        const numbers = selectedContacts.map(c => c.phone.replace(/\s/g, ''));
        const separator = ';';
        const href = `smsto:${numbers.join(separator)}?body=${encodeURIComponent(inviteText)}`;
        window.location.href = href;
    };

    // Abre share sheet nativo: usuario elige app (WhatsApp, email, etc.)
    // Orden de preferencia:
    //   1) @capacitor/share  → garantiza el share sheet nativo en Android/iOS.
    //   2) navigator.share   → Web Share API (algunos navegadores y WebViews).
    //   3) portapapeles      → último recurso.
    const shareNative = async () => {
        const selectedContacts = contacts.filter(c => selected.has(c.id));
        const namesPreview = selectedContacts.slice(0, 3).map(c => c.name).join(', ');
        const extra = selectedContacts.length > 3 ? L.andMore(selectedContacts.length - 3) : '';
        const hint = selectedContacts.length
            ? L.invitePreview(namesPreview, extra)
            : '';
        const payload = {
            title: 'KAIXO',
            text: hint + inviteText,
            url: inviteUrl,
            dialogTitle: L.shareOther
        };

        // 1) Plugin Capacitor (en APK)
        if (isNative && SharePlugin) {
            try {
                await SharePlugin.share(payload);
                return;
            } catch (e) {
                // Si el usuario cancela, el plugin lanza error — lo ignoramos.
                if (String(e?.message || '').toLowerCase().includes('cancel')) return;
                // Si falla por otra razón, caemos a navigator.share.
            }
        }

        // 2) Web Share API
        try {
            if (navigator.share) {
                await navigator.share({
                    title: payload.title,
                    text: payload.text,
                    url: payload.url
                });
                return;
            }
        } catch (e) {
            if (String(e?.name || '').toLowerCase().includes('abort')) return;
        }

        // 3) Portapapeles
        try {
            if (navigator.clipboard) {
                await navigator.clipboard.writeText(inviteText);
                alert('Enlace copiado al portapapeles');
                return;
            }
        } catch {}

        // 4) Último recurso: mostrar el texto para copia manual
        window.prompt(L.copyHint, inviteText);
    };

    return ReactDOM.createPortal(
        <div className="inv-overlay" onClick={onClose}>
            <div className="inv-modal" onClick={e => e.stopPropagation()}>
                <div className="inv-header">
                    <h3>{L.title}</h3>
                    <button className="inv-close" onClick={onClose} aria-label={L.close}>
                        <Icons.Close size={18} />
                    </button>
                </div>

                {!loading && !error && contacts.length > 0 && (
                    <div className="inv-search">
                        <Icons.Search size={15} />
                        <input
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            placeholder={L.search}
                            autoFocus={false}
                        />
                        <button className="inv-toggle-all" onClick={toggleAll}>
                            {selected.size === filtered.length && filtered.length > 0 ? L.none : L.all}
                        </button>
                    </div>
                )}

                <div className="inv-body">
                    {loading && <p className="inv-center">{L.loading}</p>}
                    {error && (
                        <div className="inv-empty">
                            <p>{error}</p>
                            <div style={{ display: 'flex', gap: 8, flexDirection: 'column', width: '100%' }}>
                                {ContactsPlugin && isNative && (
                                    <button className="inv-btn-secondary" onClick={loadContacts}>
                                        {L.retry}
                                    </button>
                                )}
                                <button className="inv-btn-primary" onClick={shareNative}>
                                    <Icons.Share size={16} color="white" /> {L.shareOther}
                                </button>
                            </div>
                        </div>
                    )}
                    {!loading && !error && contacts.length === 0 && (
                        <p className="inv-center">{L.noContacts}</p>
                    )}
                    {!loading && !error && filtered.length > 0 && filtered.map(c => (
                        <label key={c.id} className="inv-contact">
                            <input
                                type="checkbox"
                                checked={selected.has(c.id)}
                                onChange={() => toggle(c.id)}
                            />
                            <div className="inv-contact-avatar">
                                {c.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="inv-contact-body">
                                <div className="inv-contact-name">{c.name}</div>
                                <div className="inv-contact-phone">{c.phone}</div>
                            </div>
                        </label>
                    ))}
                </div>

                {!error && selected.size > 0 && (
                    <div className="inv-footer">
                        <div className="inv-selected-count">
                            {selected.size} {L.nContacts(selected.size)}
                        </div>
                        <div className="inv-actions">
                            <button className="inv-btn-secondary" onClick={sendSms}>
                                <Icons.Send size={15} /> {L.sms}
                            </button>
                            <button className="inv-btn-primary" onClick={shareNative}>
                                <Icons.Share size={15} color="white" /> {L.otherApp}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}

export default InviteFriendsModal;
